import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../services/supabase';

/**
 * =============================================
 * SAILOR AUTHENTICATION SYSTEM
 * =============================================
 * This file contains the core authentication logic for the application.
 * It manages user state, authentication operations, and profile updates.
 * 
 * CRITICAL COMPONENTS:
 * 1. Authentication state listener with fallback mechanisms
 * 2. Sign-in/sign-up/sign-out operations
 * 3. Profile management
 * 
 * DO NOT REMOVE:
 * - Fallback timeout mechanism for auth state
 * - Error handling in auth operations
 * - Loading state management
 */

// Create auth context
const AuthContext = createContext(null);

/**
 * AuthProvider component for managing authentication state
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element}
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    // =============================================
    // AUTH STATE INITIALIZATION & FALLBACK SYSTEM
    // =============================================
    // DO NOT REMOVE OR MODIFY THIS SECTION WITHOUT CAREFUL TESTING
    // This is critical for proper authentication state recovery
    
    console.log('[AuthContext Effect] Running initial effect...');
    setLoading(true); // Ensure loading is true initially
    
    // Single failsafe mechanism with improved logging
    // CRITICAL: This prevents the app from getting stuck in a loading state
    // if the Supabase auth listener fails to fire
    const failsafeTimeout = setTimeout(async () => {
      console.log('[AuthContext] CRITICAL: Auth listener failed to fire within timeout!');
      
      try {
        console.log('[AuthContext] Attempting manual session recovery...');
        const { data: { session } } = await supabase.auth.getSession();
        
        console.log('[AuthContext] Manual session check result:', 
          session ? `Session found for user ${session.user?.email}` : 'No active session');
        
        if (session?.user) {
          console.log('[AuthContext] Recovered session, setting user state');
          setUser(session.user);
        } else {
          console.log('[AuthContext] No session available, setting user to null');
          setUser(null);
        }
      } catch (err) {
        console.error('[AuthContext] Critical error during manual session recovery:', err);
        setUser(null);
        setError('Authentication system is unavailable. Please refresh the page.');
      } finally {
        console.log('[AuthContext] Failsafe recovery completed, releasing loading state');
        setLoading(false);
      }
    }, 2000); // 2 second timeout - DO NOT DECREASE THIS VALUE
    
    // --- Auth State Change Listener --- 
    // CRITICAL: This is the primary mechanism for auth state changes
    console.log('[AuthContext Effect] Setting up onAuthStateChange listener...');
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Clear the failsafe since listener fired
        clearTimeout(failsafeTimeout);
        
        console.log('[AuthContext] AUTH LISTENER FIRED! Event:', event, 
          'Session:', session ? `Active for ${session.user?.email}` : 'None');
        
        // --- TEMPORARILY DISABLED PROFILE FETCH --- 
        // FUTURE ENHANCEMENT: Consider re-enabling profile fetch here if needed
        // let userProfile = null;
        // let fetchError = null;
        // if (session?.user) { ... profile fetch try/catch ... }
        // setError(fetchError ? fetchError.message : null);
        // -------------------------------------------

        // Update user state based ONLY on session presence
        if (session?.user) {
          console.log('[AuthContext] Setting basic user state from session.');
          setUser(session.user); 
        } else {
          console.log('[AuthContext] Setting user state to null.');
          setUser(null); 
        }
        
        // Set loading to false immediately after handling session
        console.log('[AuthContext] Basic auth state determined, setting loading to false.');
        setLoading(false);
      }
    );
    
    console.log('[AuthContext Effect] Listener subscription object:', subscription);
    
    console.log('[AuthContext Effect] Listener setup complete.');
    
    // --- Cleanup --- 
    return () => {
      console.log('[AuthContext Effect Cleanup] Unsubscribing from onAuthStateChange.');
      subscription?.unsubscribe();
    };
  }, []); 
  
  /**
   * Sign in with email and password
   * CRITICAL: Maintains proper loading & error state
   * 
   * @param {string} email - User email
   * @param {string} password - User password
   */
  const signIn = async (email, password) => {
    console.log('[AuthContext] Attempting sign in for:', email);
    try {
      setLoading(true);
      setError(null);
      
      // SECURITY: Uses Supabase's built-in password auth
      // DO NOT replace with custom auth logic
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        console.error('[AuthContext] Supabase sign in error:', error);
        throw error;
      }
      
      console.log('[AuthContext] Sign in successful, data:', data);
      return data;
    } catch (err) {
      console.error('[AuthContext] Sign in function catch block error:', err);
      setError(err.message);
      throw err;
    } finally {
      console.log('[AuthContext] Sign in function finally block, setting loading false.');
      setLoading(false);
    }
  };
  
  /**
   * Sign up with email and password
   * CRITICAL: Creates both auth entry and profile record
   * 
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {Object} userData - Additional user data
   */
  const signUp = async (email, password, userData = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      // SECURITY: Uses Supabase's built-in auth
      // DO NOT implement custom registration
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData
        }
      });
      
      if (error) {
        throw error;
      }
      
      // CRITICAL: Creates profile record after successful auth
      // This is required for the application to function properly
      if (data.user) {
        try {
          // Create profile record
          const profileData = {
            user_id: data.user.id,
            username: userData.username,
            email: email,
            ...userData
          };
          
          // Only add profilePicture if provided
          if (userData.profilePicture) {
            profileData.profilePicture = userData.profilePicture;
          }
          
          // FUTURE ENHANCEMENT: Consider adding more comprehensive
          // error handling for profile creation failures
          const { error: profileError } = await supabase
            .from('user_profiles')
            .insert([profileData]);
            
          if (profileError) {
            console.error('Profile creation error:', profileError);
            // We'll continue despite profile creation error
          }
        } catch (profileErr) {
          console.error('Error in profile creation:', profileErr);
          // Continue with signup even if profile creation fails
        }
      }
      
      return data;
    } catch (err) {
      console.error('Sign up error:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Sign out the current user
   * Simple but critical for security
   */
  const signOut = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // SECURITY: Always use Supabase's signOut method
      // to ensure proper token cleanup
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw error;
      }
      
      setUser(null);
    } catch (err) {
      console.error('Sign out error:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Update user profile data
   * CRITICAL: Handles safe profile updates with column validation
   * 
   * @param {Object} updates - Profile data to update
   */
  const updateProfile = async (updates) => {
    try {
      setLoading(true);
      setError(null);
      
      if (!user) {
        throw new Error('No user logged in');
      }
      
      // CRITICAL: This section handles possible missing profilePicture column
      // DO NOT REMOVE this safety mechanism
      const safeUpdates = {...updates};
      
      // If there's an profilePicture update and we're unsure if the column exists
      if ('profilePicture' in updates) {
        try {
          // Try to update just the profilePicture first as a test
          const { error: profileUpdateError } = await supabase
            .from('user_profiles')
            .update({ profilePicture: updates.profilePicture })
            .eq('user_id', user.id);
          
          if (profileUpdateError && profileUpdateError.message.includes('does not exist')) {
            // This check might be redundant now if we know the column exists, but keeping for safety
            console.warn('profilePicture column does not exist in user_profiles table, skipping this field');
            delete safeUpdates.profilePicture;
          } else if (profileUpdateError) {
            // Re-throw other errors related to updating profilePicture
            throw profileUpdateError;
          }
        } catch (err) {
           console.error('Error during profilePicture existence check:', err);
           // Decide if we should proceed without profilePicture or throw
           delete safeUpdates.profilePicture;
        }
      }
      
      // CRITICAL: The actual profile update
      // FUTURE ENHANCEMENT: Consider adding field validation here
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update(safeUpdates)
        .eq('user_id', user.id);
        
      if (updateError) {
        throw updateError;
      }
      
      // Update local user state
      setUser({ ...user, ...safeUpdates });
      
      return { success: true };
    } catch (err) {
      console.error('Update profile error:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * AUTH CONTEXT VALUE
   * Provides authentication state and methods to the entire application
   * This structure shouldn't be changed without careful consideration
   */
  const value = {
    user,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    updateProfile
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook for accessing auth context
 * USAGE: import { useAuth } from './contexts/AuthContext'
 * 
 * @returns {Object} Authentication context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext; 